import { useEffect, useState } from 'react'
import { Send, Loader2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { useCurrentClient, useCurrentUser } from '@/features/auth/state/auth'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

import {
  type ObservationFeedItem,
  useCreateObservation,
  useFetchAllObservations,
  useFetchObservationByProductId,
  useSoftDeleteObservation
} from '@/lib/hooks/observation'
import { FeedbackDialog } from '@/components/form/feedback-dialog'
import { formatDate, pegarPrimeiroEUltimoNome } from '@/utils/format'
import { toast } from '@/lib/hooks/use-toast'
import { TypeViewProduct } from '@/features/products/state/selectProduct'
import ButtonConfirmDelet from '@/components/ui/button-confirm-delet'
import { SelectValue } from '@radix-ui/react-select'

enum Category {
  FINANCIAL = 'FINACEIRO',
  OPERATIONAL = 'OPERACIONAL',
  PADRAO = 'PADRAO',
  CLIENT = 'CLIENTE'
}

const allCategories = Object.values(Category)

type AuthContext = {
  id?: string
  id_tenant?: string
  name?: string
  typeUser?: string
  typeCargo?: string
  isClient: boolean
}

const canSend = (typeUser: string, typeCargo: string, category: Category, isClient = false) => {
  if (isClient) return category === Category.CLIENT
  if (typeUser === 'USUARIO') return category === Category.PADRAO
  if (typeUser === 'ADMIN') return [Category.OPERATIONAL, Category.PADRAO].includes(category)
  if (typeCargo === 'FINANCEIRO') return [Category.FINANCIAL, Category.OPERATIONAL].includes(category)
  return false
}

const canView = (typeUser: string, typeCargo: string, category: Category, isClient = false) => {
  if (isClient) return [Category.PADRAO, Category.CLIENT].includes(category)
  if (typeUser === 'USUARIO') return [Category.PADRAO, Category.CLIENT].includes(category)
  if (typeUser === 'USUARIO' && typeCargo === 'CEO')
    return [Category.OPERATIONAL, Category.PADRAO, Category.CLIENT].includes(category)
  if (typeUser === 'ADMIN') return [Category.OPERATIONAL, Category.PADRAO, Category.CLIENT].includes(category)
  if (typeCargo === 'FINANCEIRO') return [Category.PADRAO, Category.CLIENT].includes(category)
  return false
}

type Message = {
  id: number | string | null
  mensage: string
  sender: string
  category: Category
  createdDate: Date | string
  id_user: string | null
  isAlerta?: boolean
  sourceType?: 'OBSERVATION' | 'ALERT'
  originScope?: 'PRODUCT' | 'CLIENT' | 'TENANT'
  originLabel?: string | null
  originDescription?: string | null
  companyName?: string | null
  companyDocument?: string | null
  productName?: string | null
  productStatus?: string | null
  id_produto?: string | null
  canDelete?: boolean
}

type SortOrder = 'desc' | 'asc'

export default function ObservationClient({ type, cnpj, id }: { type: TypeViewProduct; cnpj: string; id?: string }) {
  const user = useCurrentUser()
  const client = useCurrentClient()
  const [messageContent, setMessageContent] = useState('')
  const [category, setCategory] = useState<Category>(client ? Category.CLIENT : Category.PADRAO)
  const [filter, setFilter] = useState<Category | 'all'>('all')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isComposeExpanded, setIsComposeExpanded] = useState(false)
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const { mutate: createObservation } = useCreateObservation()
  const { mutate: deleteObservation } = useSoftDeleteObservation()

  const { data: geralData = [], isLoading: loadingGeral, error: errorGeral } = useFetchAllObservations(cnpj)
  const { data: produtoData = [], isLoading: loadingProduto, error: errorProduto } = useFetchObservationByProductId(id)

  const observation = type === 'GERAL' ? geralData : produtoData
  const isLoading = type === 'GERAL' ? loadingGeral : loadingProduto
  const error = type === 'GERAL' ? errorGeral : errorProduto

  const messages = (observation ?? []) as ObservationFeedItem[]
  const authContext: AuthContext = client
    ? {
        id: client.id,
        id_tenant: client.id_tenant,
        name: client.name,
        isClient: true
      }
    : {
        id: user?.id,
        id_tenant: user?.id_tenant,
        name: user?.name,
        typeUser: user?.typeUser,
        typeCargo: user?.typeCargo,
        isClient: false
      }

  useEffect(() => {
    if (authContext.isClient) {
      setCategory(Category.CLIENT)
    }
  }, [authContext.isClient])

  useEffect(() => {
    if (
      filter !== 'all' &&
      !canView(authContext.typeUser ?? '', authContext.typeCargo ?? '', filter as Category, authContext.isClient)
    ) {
      setFilter('all')
    }
  }, [authContext.isClient, authContext.typeCargo, authContext.typeUser, filter])

  if (isLoading) return <Loading />
  if (error) return <Error message={error.message} />

  const handleSendMessage = (sendAlert = false) => {
    if (messageContent.trim() === '') return

    const isAuthorized = canSend(authContext.typeUser ?? '', authContext.typeCargo ?? '', category, authContext.isClient)
    if (!isAuthorized) {
      toast({
        title: 'Permissão negada',
        description: 'Você não tem permissão para enviar essa categoria de mensagem.',
        variant: 'destructive'
      })
      return
    }
    const newMessage = {
      mensage: messageContent,
      cnpj,
      sender: authContext.name ?? 'Desconhecido',
      id_user: authContext.id,
      id_tenant: authContext.id_tenant,
      id_produto: id || undefined,
      isAlerta: sendAlert,
      category: category as unknown as 'OPERACIONAL' | 'PADRAO' | 'CLIENTE' | 'FINANCEIRO'
    }

    createObservation(newMessage, {
      onSuccess: () => {
        setMessage({
          type: 'success',
          text: sendAlert ? 'Observação enviada e alerta criado com sucesso!' : 'Enviado a observação com sucesso!'
        })
        setIsDialogOpen(true)
        setIsComposeExpanded(false)
        setMessageContent('')
      },
      onError: error => {
        console.error('Erro ao adicionar regime e produtos:', error)
        setMessage({ type: 'error', text: 'Houve um erro ao enviar a observação.' })
        setIsDialogOpen(true)
      }
    })
  }

  const handleDeleteMessage = (id: number | null) => {
    deleteObservation(id, {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Observação deletada com sucesso!' })
        setIsDialogOpen(true)
      },
      onError: error => {
        console.error('Erro ao deletar mensagem:', error)
        setMessage({ type: 'error', text: 'Erro ao deletar mensagem.' })
        setIsDialogOpen(true)
      }
    })
  }
  // const formatDateTime = (date: Date | string | null | undefined) => {
  //   try {
  //     const d = new Date(date ?? '')
  //     if (isNaN(d.getTime())) return 'Data inválida'
  //     return new Intl.DateTimeFormat('pt-BR', {
  //       day: '2-digit',
  //       month: '2-digit',
  //       year: 'numeric',
  //       hour: '2-digit',
  //       minute: '2-digit'
  //     }).format(d)
  //   } catch {
  //     return 'Data inválida'
  //   }
  // }

  const getCategoryColor = (category: Category) => {
    switch (category) {
      case Category.FINANCIAL:
        return 'bg-green-500 hover:bg-green-600'
      case Category.OPERATIONAL:
        return 'bg-blue-500 hover:bg-blue-600'
      case Category.CLIENT:
        return 'bg-purple-500 hover:bg-purple-600'
      default:
        return 'bg-gray-500 hover:bg-gray-600'
    }
  }

  const getCategoryName = (category: Category): string => {
    const categoryNameMap: Record<Category, string> = {
      [Category.FINANCIAL]: 'Financeiro',
      [Category.OPERATIONAL]: 'Operacional',
      [Category.CLIENT]: 'Cliente',
      [Category.PADRAO]: 'Padrão'
    }

    return categoryNameMap[category] || 'Desconhecido'
  }

  const normalizeCategory = (message: ObservationFeedItem): Category => {
    if (allCategories.includes(message.category as Category)) {
      return message.category as Category
    }
    return message.sourceType === 'ALERT' ? Category.PADRAO : Category.PADRAO
  }

  const normalizedMessages: Message[] = messages.map(message => ({
    ...message,
    category: normalizeCategory(message)
  }))

  const visibleMessages = normalizedMessages.filter(msg =>
    filter === 'all'
      ? canView(authContext.typeUser ?? '', authContext.typeCargo ?? '', msg.category, authContext.isClient)
      : msg.category === filter &&
        canView(authContext.typeUser ?? '', authContext.typeCargo ?? '', msg.category, authContext.isClient)
  )

  const sortedMessages = [...visibleMessages].sort((a: any, b: any) => {
    const dateA = new Date(a.createdDate).getTime()
    const dateB = new Date(b.createdDate).getTime()
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
  })

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="bg-background shadow-sm">
        {/* Header */}
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 py-3 px-4 border-b">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">Observações</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {visibleMessages.length}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto md:flex-row ">
            <Select value={filter} onValueChange={value => setFilter(value as Category | 'all')}>
              <SelectTrigger className="min-w-[120px] h-8 flex-1 text-foreground">
                <SelectValue placeholder="Todas" className="text-foreground" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {allCategories
                  .filter(cat =>
                    canView(authContext.typeUser ?? '', authContext.typeCargo ?? '', cat, authContext.isClient)
                  )
                  .map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {getCategoryName(cat)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Button
              onClick={() => setSortOrder(current => (current === 'desc' ? 'asc' : 'desc'))}
              size="sm"
              variant="outline"
              className="h-8 flex-1 sm:flex-none"
            >
              {sortOrder === 'desc' ? 'Mais recentes' : 'Mais antigas'}
            </Button>

            <Button
              onClick={() => setIsComposeExpanded(!isComposeExpanded)}
              size="sm"
              variant="outline"
              className="h-8 flex-1 sm:flex-none"
            >
              <Send className="mr-1 h-3 w-3" />
              {authContext.isClient ? 'Nova observação' : 'Nova'}
            </Button>
          </div>
        </CardHeader>

        {/* Messages Area */}
        <CardContent className="p-0">
          {!isComposeExpanded ? (
            <ScrollArea className="h-[250px] p-4">
              {sortedMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <p className="text-sm">Nenhuma mensagem encontrada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedMessages.map((message: Message) => (
                    <MessageCard
                      key={message.id}
                      message={message}
                      userId={authContext.id}
                      onDelete={handleDeleteMessage}
                      getCategoryColor={getCategoryColor}
                      getCategoryName={getCategoryName}
                      formatDateTime={formatDate}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          ) : null}
        </CardContent>

        {/* Compose Area - only show when expanded */}
        {isComposeExpanded && (
          <CardFooter className="p-4 border-t bg-background ">
            <div className="w-full space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{authContext.isClient ? 'Nova observação' : 'Nova mensagem'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsComposeExpanded(false)
                    setMessageContent('')
                  }}
                  className="h-8 flex-1 sm:flex-none"
                >
                  <span>Fechar</span>
                </Button>
              </div>

              <Textarea
                placeholder="Escreva sua mensagem..."
                value={messageContent}
                onChange={e => setMessageContent(e.target.value)}
                className="min-h-[80px] resize-none"
              />

              <div className="flex items-center justify-between gap-2 flex-col md:flex-row">
                <div className="flex items-center gap-2">
                  <Label className="text-xs font-sans">Categoria:</Label>
                  <RadioGroup
                    value={category}
                    onValueChange={value => setCategory(value as Category)}
                    className="flex gap-2"
                  >
                    {allCategories
                      .filter(cat =>
                        canSend(authContext.typeUser ?? '', authContext.typeCargo ?? '', cat, authContext.isClient)
                      )
                      .map(cat => (
                        <div className="flex items-center space-x-1" key={cat}>
                          <RadioGroupItem
                            value={cat}
                            id={cat}
                            className={cn(
                              'sr-only' // esconde o círculo padrão
                            )}
                          />
                          <Label
                            htmlFor={cat}
                            className={cn(
                              'text-xs px-1 py-1 rounded-full border cursor-pointer transition-colors',
                              category === cat
                                ? `${getCategoryColor(cat)} text-white`
                                : 'bg-white text-gray-700 border-gray-300'
                            )}
                          >
                            {getCategoryName(cat)}
                          </Label>
                        </div>
                      ))}
                  </RadioGroup>
                </div>

                <div className="flex gap-2">
                  {!authContext.isClient && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendMessage(true)}
                      className="h-8 flex-1 sm:flex-none"
                      disabled={messageContent.trim() === ''}
                    >
                      <AlertTriangle className="mr-1 h-3 w-3" />
                      Enviar e alertar
                    </Button>
                  )}
                  {canSend(authContext.typeUser ?? '', authContext.typeCargo ?? '', category, authContext.isClient) && (
                    <Button
                      onClick={() => handleSendMessage(false)}
                      disabled={messageContent.trim() === ''}
                      size="sm"
                      className="h-8 flex-1 sm:flex-none"
                    >
                      <Send className="mr-1 h-3 w-3" />
                      {authContext.isClient ? 'Enviar observação' : 'Enviar'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardFooter>
        )}
      </Card>
      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />
    </div>
  )
}

function MessageCard({
  message,
  userId,
  onDelete,
  getCategoryColor,
  getCategoryName,
  formatDateTime
}: {
  message: Message
  userId?: string
  onDelete: (id: number | null) => void
  getCategoryColor: (category: Category) => string
  getCategoryName: (category: Category) => string
  formatDateTime: (date: Date | string | null | undefined) => string
}) {
  const canDeleteMessage =
    message.canDelete !== false &&
    message.sourceType !== 'ALERT' &&
    typeof message.id === 'number' &&
    message.id_user === userId

  return (
    <div className="border rounded-lg p-3 bg-card hover:bg-muted/50 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-medium text-sm">{pegarPrimeiroEUltimoNome(message.sender)}</span>
            <Badge className={cn(getCategoryColor(message.category), 'text-xs px-2 py-0')}>
              {getCategoryName(message.category)}
            </Badge>
            {message.sourceType === 'ALERT' && (
              <Badge variant="outline" className="text-xs px-2 py-0">
                Alerta
              </Badge>
            )}
          </div>
          {message.sourceType === 'ALERT' && message.originLabel && (
            <p className="text-xs text-muted-foreground mb-1">{message.originLabel}</p>
          )}
          {message.originDescription && <p className="text-xs text-muted-foreground mb-2">{message.originDescription}</p>}
          <p className="text-sm text-muted-foreground leading-relaxed">{message.mensage}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-muted-foreground">{formatDateTime(message.createdDate)}</span>
          {canDeleteMessage && (
            // dispara o trigger com o ícone de lixo padrão
            <ButtonConfirmDelet
              confirmText="Excluir mensagem?"
              successBtnText="Excluir"
              onSuccess={() => onDelete(message.id as number | null)}
              isDisabled={!onDelete}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function Error({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-10 text-red-500 font-semibold">Erro ao carregar: {message}</div>
  )
}
function Loading() {
  return (
    <div className="flex items-center justify-center py-10 text-foreground">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  )
}
